import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api, qs } from '../lib/api'
import { useAuth } from '../lib/auth'
import { ago, join } from '../lib/format'
import type { Fleet, LinkedDevice, ListResponse, Vehicle } from '../lib/types'
import { Page } from '../components/Shell'
import { DevicePicker } from '../components/DevicePicker'
import { FleetManager } from '../components/FleetManager'
import { Icon } from '../components/Icon'
import { normalizeVehicleIcon, VEHICLE_ICONS, VehicleIcon } from '../components/VehicleIcon'
import { DataTable, Toolbar, useSearch, type Column } from '../components/DataTable'
import { Button, Chip, Field, Modal, Panel, StateChip, toast } from '../components/ui'

export function Vehicles() {
  const { can } = useAuth()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const search = useSearch()
  const [fleet, setFleet] = useState('')
  const [offset, setOffset] = useState(0)
  const [sort, setSort] = useState({ key: 'name', desc: false })
  const [editing, setEditing] = useState<Vehicle | null>(null)
  const [creating, setCreating] = useState(false)
  const [fleetsOpen, setFleetsOpen] = useState(false)

  const limit = 50

  const list = useQuery({
    queryKey: ['vehicles', search.debounced, fleet, offset, sort],
    queryFn: () =>
      api<ListResponse<Vehicle>>(
        `/vehicles${qs({
          q: search.debounced,
          fleetId: fleet,
          limit,
          offset,
          sort: sort.key,
          order: sort.desc ? 'desc' : 'asc',
        })}`,
      ),
  })

  const fleets = useQuery({ queryKey: ['fleets'], queryFn: () => api<{ items: Fleet[] }>('/fleets') })

  const remove = useMutation({
    mutationFn: (id: string) => api(`/vehicles/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast('Vehicle deleted', 'ok')
      void qc.invalidateQueries({ queryKey: ['vehicles'] })
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  const columns: Column<Vehicle>[] = [
    {
      key: 'name',
      header: 'Vehicle',
      sort: 'name',
      render: (v) => (
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="text-[var(--color-ink-3)]">
            <VehicleIcon icon={v.icon} size={18} />
          </span>
          <div className="min-w-0">
            <div className="text-[var(--color-ink)] truncate">{v.name}</div>
            <div className="text-[11px] text-[var(--color-ink-4)] truncate">
              {join([v.registration, v.make && `${v.make} ${v.model ?? ''}`.trim()])}
            </div>
          </div>
        </div>
      ),
    },
    { key: 'state', header: 'Status', sort: 'state', width: '120px', render: (v) => <StateChip state={v.state} /> },
    {
      key: 'fleet',
      header: 'Fleet',
      sort: 'fleet',
      render: (v) => v.fleetName ?? <span className="text-[var(--color-ink-4)]">Unassigned</span>,
    },
    {
      key: 'driver',
      header: 'Driver',
      render: (v) => v.driverName ?? <span className="text-[var(--color-ink-4)]">—</span>,
    },
    {
      key: 'device',
      header: 'Device',
      render: (v) =>
        v.imei ? (
          <span className="tnum text-[12px]">{v.imei}</span>
        ) : (
          <Chip tone="warn">No hardware</Chip>
        ),
    },
    {
      key: 'seen',
      header: 'Last seen',
      numeric: true,
      width: '110px',
      render: (v) => <span className="text-[11px]">{ago(v.stateSince)}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '92px',
      render: (v) => (
        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <button
            className="btn btn-ghost btn-sm px-1.5"
            title="Replay"
            onClick={() => navigate(`/vehicles/${v.id}`)}
          >
            <Icon name="route" size={13} />
          </button>
          {can('vehicle.manage') && (
            <>
              <button className="btn btn-ghost btn-sm px-1.5" title="Edit" onClick={() => setEditing(v)}>
                <Icon name="edit" size={13} />
              </button>
              <button
                className="btn btn-ghost btn-sm px-1.5"
                title="Delete"
                style={{ color: 'var(--text-critical)' }}
                onClick={() => {
                  if (confirm(`Delete ${v.name}? Its history is kept, but it disappears from the fleet.`)) {
                    remove.mutate(v.id)
                  }
                }}
              >
                <Icon name="trash" size={13} />
              </button>
            </>
          )}
        </div>
      ),
    },
  ]

  return (
    <Page
      title="Vehicles"
      subtitle={list.data ? `${list.data.total} in this account` : undefined}
      actions={
        <>
          {can('fleet.manage') && (
            <Button onClick={() => setFleetsOpen(true)} title="Create, rename or remove this account's fleets">
              <Icon name="polygon" size={13} />
              Fleets
            </Button>
          )}
          {can('vehicle.manage') && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Icon name="plus" size={13} />
              Add vehicle
            </Button>
          )}
        </>
      }
    >
      <Toolbar search={search.value} onSearch={search.setValue} placeholder="Name, registration, VIN…">
        <select className="field w-auto" value={fleet} onChange={(e) => setFleet(e.target.value)}>
          <option value="">All fleets</option>
          {fleets.data?.items.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
      </Toolbar>

      <Panel padded={false} title={undefined}>
        <DataTable
          columns={columns}
          rows={list.data?.items}
          rowKey={(v) => v.id}
          loading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
          onRowClick={(v) => navigate(`/vehicles/${v.id}`)}
          total={list.data?.total}
          limit={limit}
          offset={offset}
          onPage={setOffset}
          sort={sort}
          onSort={(key, desc) => setSort({ key, desc })}
          empty={{
            title: 'No vehicles yet',
            body: 'Add a vehicle and fit it with a device to start tracking.',
            action: can('vehicle.manage') ? (
              <Button variant="primary" onClick={() => setCreating(true)}>
                Add the first vehicle
              </Button>
            ) : undefined,
          }}
        />
      </Panel>

      {fleetsOpen && <FleetManager onClose={() => setFleetsOpen(false)} />}

      {(creating || editing) && (
        <VehicleForm
          vehicle={editing}
          onClose={() => {
            setCreating(false)
            setEditing(null)
          }}
          onSaved={() => {
            setCreating(false)
            setEditing(null)
            void qc.invalidateQueries({ queryKey: ['vehicles'] })
          }}
        />
      )}
    </Page>
  )
}

/* ===========================================================================
   Create / edit
   =========================================================================== */

function VehicleForm({
  vehicle,
  onClose,
  onSaved,
}: {
  vehicle: Vehicle | null
  onClose: () => void
  onSaved: () => void
}) {
  const [form, setForm] = useState({
    name: vehicle?.name ?? '',
    registration: vehicle?.registration ?? '',
    vin: vehicle?.vin ?? '',
    make: vehicle?.make ?? '',
    model: vehicle?.model ?? '',
    year: vehicle?.year?.toString() ?? '',
    vehicleType: vehicle?.vehicleType ?? 'van',
    // The map glyph. Follows the type until the operator picks one.
    icon: normalizeVehicleIcon(vehicle ? vehicle.icon : 'van'),
    fleetId: vehicle?.fleetId ?? '',
    deviceId: vehicle?.deviceId ?? '',
    speedLimitKph: vehicle?.speedLimitKph?.toString() ?? '',
    idleThresholdS: vehicle?.idleThresholdS?.toString() ?? '',
    odometerKm: '',
    camera: '',
    recovery: '',
  })
  const { can } = useAuth()
  const canRecovery = can('vehicle.recovery')

  // The list row does not carry the additional units; the detail does.
  const detail = useQuery({
    queryKey: ['vehicle', vehicle?.id],
    queryFn: () => api<Vehicle>(`/vehicles/${vehicle!.id}`),
    enabled: Boolean(vehicle),
  })
  const linked = (role: LinkRole) => detail.data?.linkedDevices?.find((l) => l.role === role)
  const [linksLoaded, setLinksLoaded] = useState(false)
  useEffect(() => {
    if (linksLoaded || !detail.data) return
    setForm((f) => ({
      ...f,
      camera: linked('camera')?.deviceId ?? '',
      recovery: linked('recovery')?.deviceId ?? '',
    }))
    setLinksLoaded(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.data, linksLoaded])

  const fleets = useQuery({ queryKey: ['fleets'], queryFn: () => api<{ items: Fleet[] }>('/fleets') })

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: form.name.trim(),
        registration: form.registration.trim(),
        vin: form.vin.trim(),
        make: form.make.trim(),
        model: form.model.trim(),
        year: form.year ? Number(form.year) : null,
        vehicleType: form.vehicleType,
        icon: form.icon,
        fleetId: form.fleetId || null,
        deviceId: form.deviceId || null,
        clearDevice: Boolean(vehicle?.deviceId) && !form.deviceId,
        speedLimitKph: form.speedLimitKph ? Number(form.speedLimitKph) : null,
        idleThresholdS: form.idleThresholdS ? Number(form.idleThresholdS) : null,
        odometerKm: form.odometerKm ? Number(form.odometerKm) : null,
      }
      const saved = vehicle
        ? (await api(`/vehicles/${vehicle.id}`, { method: 'PATCH', body }), vehicle)
        : await api<Vehicle>('/vehicles', { body })

      // Additional units are fitted separately, after the vehicle exists.
      const roles: LinkRole[] = canRecovery ? ['camera', 'recovery'] : ['camera']
      for (const role of roles) {
        const before = linked(role)?.deviceId ?? ''
        const after = form[role]
        if (before === after) continue
        if (after) {
          await api(`/vehicles/${saved.id}/devices/${role}`, { method: 'PUT', body: { deviceId: after } })
        } else {
          await api(`/vehicles/${saved.id}/devices/${role}`, { method: 'DELETE' })
        }
      }
    },
    onSuccess: () => {
      toast(vehicle ? 'Vehicle updated' : 'Vehicle added', 'ok')
      onSaved()
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  return (
    <Modal
      open
      onClose={onClose}
      title={vehicle ? `Edit ${vehicle.name}` : 'Add vehicle'}
      width={600}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!form.name.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : vehicle ? 'Save changes' : 'Add vehicle'}
          </Button>
        </>
      }
    >
      <div className="grid sm:grid-cols-2 gap-3.5">
        <div className="sm:col-span-2">
          <Field label="Name" required hint="What dispatchers call it — “Van 14”, not the VIN.">
            <input className="field" value={form.name} onChange={set('name')} autoFocus />
          </Field>
        </div>

        <Field label="Registration">
          <input className="field" value={form.registration} onChange={set('registration')} placeholder="AB-123-CD" />
        </Field>
        <Field label="VIN">
          <input className="field tnum" value={form.vin} onChange={set('vin')} maxLength={17} />
        </Field>

        <Field label="Make">
          <input className="field" value={form.make} onChange={set('make')} placeholder="Ford" />
        </Field>
        <Field label="Model">
          <input className="field" value={form.model} onChange={set('model')} placeholder="Transit" />
        </Field>

        <Field label="Year">
          <input className="field tnum" type="number" value={form.year} onChange={set('year')} min={1950} max={2100} />
        </Field>
        <Field label="Type">
          <select
            className="field"
            value={form.vehicleType}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                vehicleType: e.target.value,
                // An icon that was just mirroring the type keeps doing so;
                // one the operator chose deliberately stays put.
                icon: f.icon === normalizeVehicleIcon(f.vehicleType) ? normalizeVehicleIcon(e.target.value) : f.icon,
              }))
            }
          >
            {['car', 'van', 'truck', 'bus', 'trailer', 'asset', 'motorcycle'].map((t) => (
              <option key={t} value={t}>
                {t[0]!.toUpperCase() + t.slice(1)}
              </option>
            ))}
          </select>
        </Field>

        <div className="sm:col-span-2">
          <span className="label block mb-1.5">Map icon</span>
          <div className="grid grid-cols-6 gap-1.5" role="radiogroup" aria-label="Map icon">
            {VEHICLE_ICONS.map((d) => {
              const active = form.icon === d.id
              return (
                <button
                  key={d.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  title={d.label}
                  onClick={() => setForm((f) => ({ ...f, icon: d.id }))}
                  className="flex flex-col items-center gap-1 py-2 px-1 rounded-[3px] border transition-colors"
                  style={{
                    borderColor: active ? 'var(--accent)' : 'var(--color-rule)',
                    background: active ? 'var(--accent-dim)' : 'transparent',
                    color: active ? 'var(--accent-text)' : 'var(--color-ink-2)',
                  }}
                >
                  <VehicleIcon icon={d.id} size={22} />
                  <span className="text-[10px] leading-tight text-center truncate w-full">{d.label}</span>
                </button>
              )
            })}
          </div>
          <span className="block mt-1 text-[11px] text-[var(--color-ink-4)]">
            Drawn inside the vehicle's status dot on the live map and as the replay head.
          </span>
        </div>

        <Field label="Fleet" hint="Groups vehicles for filters, reports and user access. Create fleets with the Fleets button on the Vehicles page.">
          <select className="field" value={form.fleetId} onChange={set('fleetId')}>
            <option value="">Unassigned</option>
            {fleets.data?.items.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Tracking device"
          hint={
            vehicle?.imei
              ? `Currently fitted: ${vehicle.imei} — click ✕ to remove it, or search to swap it`
              : 'Type to search by IMEI, serial, model or SIM — only unfitted units are offered'
          }
        >
          <DevicePicker
            value={form.deviceId}
            current={vehicle?.deviceId && vehicle.imei ? { id: vehicle.deviceId, imei: vehicle.imei } : undefined}
            exclude={[form.camera, form.recovery]}
            capability="tracking"
            onChange={(id) => setForm((f) => ({ ...f, deviceId: id }))}
          />
        </Field>

        <div className="sm:col-span-2 rule-t pt-3.5 mt-1">
          <div className="label mb-3">Additional units — stored beside the tracking device, never mixed into its trips</div>
          <div className="grid sm:grid-cols-2 gap-3.5">
            <LinkPicker
              label="Camera (MDVR)"
              hint="A dashcam in the cabin; its GPS is kept but does not drive tracking"
              value={form.camera}
              current={linked('camera')}
              exclude={[form.deviceId, form.recovery]}
              capability="video"
              onChange={(v) => setForm((f) => ({ ...f, camera: v }))}
            />
            {canRecovery && (
              <LinkPicker
                label="Stolen-vehicle recovery unit"
                hint="Hidden from the client's own users"
                value={form.recovery}
                current={linked('recovery')}
                exclude={[form.deviceId, form.camera]}
                capability="recovery"
                onChange={(v) => setForm((f) => ({ ...f, recovery: v }))}
              />
            )}
          </div>
        </div>

        <div className="sm:col-span-2 rule-t pt-3.5 mt-1">
          <div className="label mb-3">Thresholds — leave blank to use the account defaults</div>
          <div className="grid sm:grid-cols-3 gap-3.5">
            <Field label="Speed limit" hint="km/h">
              <input className="field tnum" type="number" value={form.speedLimitKph} onChange={set('speedLimitKph')} placeholder="120" />
            </Field>
            <Field label="Idle threshold" hint="seconds">
              <input className="field tnum" type="number" value={form.idleThresholdS} onChange={set('idleThresholdS')} placeholder="180" />
            </Field>
            <Field label="Odometer" hint="km, from the dashboard">
              <input className="field tnum" type="number" value={form.odometerKm} onChange={set('odometerKm')} placeholder="—" />
            </Field>
          </div>
        </div>
      </div>
    </Modal>
  )
}

type LinkRole = 'camera' | 'recovery'

function LinkPicker({
  label,
  hint,
  value,
  current,
  exclude,
  capability,
  onChange,
}: {
  label: string
  hint: string
  value: string
  current?: LinkedDevice
  exclude: string[]
  /** Only units recorded as able to do this job are offered. */
  capability: 'video' | 'recovery'
  onChange: (id: string) => void
}) {
  return (
    <Field label={label} hint={hint}>
      <DevicePicker
        value={value}
        current={current ? { id: current.deviceId, imei: current.imei, modelName: current.modelName ?? undefined } : undefined}
        exclude={exclude}
        capability={capability}
        onChange={onChange}
      />
    </Field>
  )
}
